import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { automationsAPI } from '@/shared/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { AutomationForm } from '../components/AutomationForm';
import { useAutomationModals } from '@/shared/hooks/useAutomationModals';
import {
  Zap,
  Plus,
  Play,
  Pause,
  Trash2,
  Edit,
  Calendar,
  BarChart3,
  Eye,
  Timer,
  TrendingUp,
  Clock,
  Webhook,
  LayoutGrid,
  Table as TableIcon,
} from 'lucide-react';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  SortableTable,
} from '@travellegacy/nexops-design-system';
import { formatRelativeTime, formatPercentage, formatTimeSaved } from '@/shared/lib/utils';
import type { AutomationWithStats } from '@/shared/types';
import { AutomationType } from '@/shared/types';
import { Button } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { AutomationTypeBadge } from '../components/AutomationTypeBadge';

export default function Automations() {
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | AutomationType>('all');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>(() => {
    // Load from localStorage
    const saved = localStorage.getItem('automations-view-mode');
    return (saved as 'grid' | 'table') || 'table';
  });

  // Save to localStorage when view mode changes
  useEffect(() => {
    localStorage.setItem('automations-view-mode', viewMode);
  }, [viewMode]);

  // Use shared modals hook
  const {
    modals,
    openExecuteModal,
    openToggleModal,
    openDeleteModal,
    openEditModal,
    isExecuting,
    isToggling,
    isDeleting,
  } = useAutomationModals();

  // Fetch automations (OTIMIZADO - usa endpoint agregado)
  const { data: automations, isLoading } = useQuery({
    queryKey: ['automations-aggregated', statusFilter],
    queryFn: async () => {
      const params = statusFilter === 'all' ? {} : { isActive: statusFilter === 'active' };
      const response = await automationsAPI.listAggregated(params);
      return response.data.data as AutomationWithStats[];
    },
    staleTime: 5 * 60 * 1000, // 5 minutos
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        {/* Header Skeleton */}
        <div className="flex items-center justify-between">
          <div>
            <div className="h-9 w-48 bg-muted animate-pulse rounded" />
            <div className="h-5 w-96 bg-muted animate-pulse rounded mt-1" />
          </div>
          <div className="h-10 w-36 bg-muted animate-pulse rounded" />
        </div>

        {/* Filters Skeleton */}
        <div className="flex flex-wrap items-center gap-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-8 w-24 bg-muted animate-pulse rounded-full" />
          ))}
        </div>

        {/* Automations Grid Skeleton */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-card rounded-lg border p-6 animate-pulse">
              <div className="h-6 w-32 bg-muted rounded mb-2" />
              <div className="h-8 w-48 bg-muted rounded mb-4" />
              <div className="space-y-2">
                <div className="h-4 w-full bg-muted rounded" />
                <div className="h-4 w-3/4 bg-muted rounded" />
              </div>
              <div className="mt-6 pt-4 border-t space-y-2">
                <div className="h-4 w-full bg-muted rounded" />
                <div className="h-4 w-full bg-muted rounded" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Apply type filter
  const filteredAutomations = (automations || []).filter((automation) => {
    if (typeFilter === 'all') return true;
    return automation.type === typeFilter;
  });

  const schedulerCount = automations?.filter((a) => a.type === AutomationType.CRON).length || 0;
  const eventCount = automations?.filter((a) => a.type === AutomationType.WEBHOOK).length || 0;

  // Calculate status counts based on current type filter
  const getFilteredAutomationsByType = () => {
    if (typeFilter === 'all') return automations || [];
    return (automations || []).filter((a) => a.type === typeFilter);
  };

  const typeFilteredAutomations = getFilteredAutomationsByType();
  const statusCounts = {
    all: typeFilteredAutomations.length,
    active: typeFilteredAutomations.filter((a) => a.isActive).length,
    inactive: typeFilteredAutomations.filter((a) => !a.isActive).length,
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Automações</h1>
          <p className="text-muted-foreground mt-1">Gerencie suas automações e workflows</p>
        </div>
        <div className="flex items-center gap-2">
          {/* View Mode Toggle */}
          <div className="flex items-center border border-border rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'grid'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Visualização em grade"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded transition-colors ${
                viewMode === 'table'
                  ? 'bg-primary text-white'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              title="Visualização em tabela"
            >
              <TableIcon className="h-4 w-4" />
            </button>
          </div>
          <Button onClick={() => setIsCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Automação
          </Button>
        </div>
      </div>

      {/* Filters - Chips Style */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Type Filters */}
        <button
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            typeFilter === 'all'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={() => setTypeFilter('all')}
        >
          Todos ({automations?.length || 0})
        </button>
        <button
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            typeFilter === AutomationType.CRON
              ? 'bg-blue-600 text-white dark:bg-blue-600 shadow-sm'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={() => setTypeFilter(AutomationType.CRON)}
        >
          <Calendar className="h-3.5 w-3.5" />
          Schedulers ({schedulerCount})
        </button>
        <button
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            typeFilter === AutomationType.WEBHOOK
              ? 'bg-purple-600 text-white dark:bg-purple-600 shadow-sm'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={() => setTypeFilter(AutomationType.WEBHOOK)}
        >
          <Webhook className="h-3.5 w-3.5" />
          Events ({eventCount})
        </button>

        {/* Divider */}
        <div className="h-6 w-px bg-border mx-1" />

        {/* Status Filters */}
        <button
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            statusFilter === 'all'
              ? 'bg-primary text-white shadow-sm'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={() => setStatusFilter('all')}
        >
          Todas ({statusCounts.all})
        </button>
        <button
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            statusFilter === 'active'
              ? 'bg-green-600 text-white dark:bg-green-600 shadow-sm'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={() => setStatusFilter('active')}
        >
          Ativas ({statusCounts.active})
        </button>
        <button
          className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
            statusFilter === 'inactive'
              ? 'bg-red-600 text-white dark:bg-red-600 shadow-sm'
              : 'bg-muted text-muted-foreground hover:bg-muted/80'
          }`}
          onClick={() => setStatusFilter('inactive')}
        >
          Inativas ({statusCounts.inactive})
        </button>
      </div>

      {/* Automations View */}
      {filteredAutomations.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Zap className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-card-foreground mb-2">
                Nenhuma automação encontrada
              </h3>
              <p className="text-muted-foreground mb-4">Crie sua primeira automação para começar</p>
              <Button onClick={() => setIsCreateOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Criar Automação
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : viewMode === 'grid' ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filteredAutomations.map((automation) => (
            <Card key={automation.id} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <AutomationTypeBadge type={automation.type} size="sm" />
                      <Badge variant={automation.isActive ? 'success' : 'error'}>
                        {automation.isActive ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg flex items-center space-x-2">
                      <span
                        className={`${
                          automation.type === AutomationType.CRON
                            ? 'text-blue-700 dark:text-blue-400'
                            : 'text-purple-700 dark:text-purple-400'
                        }`}
                      >
                        {automation.name}
                      </span>
                    </CardTitle>
                    {automation.description && (
                      <p className="text-sm text-muted-foreground mt-2">{automation.description}</p>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center text-muted-foreground">
                      <BarChart3 className="h-4 w-4 mr-1" />
                      <span>Execuções</span>
                    </div>
                    <span className="font-medium text-card-foreground">
                      {automation.stats.totalExecutions}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <div className="flex items-center text-muted-foreground">
                      <TrendingUp className="h-4 w-4 mr-1" />
                      <span>Taxa de Sucesso</span>
                    </div>
                    <span
                      className={
                        automation.stats.successRate >= 90
                          ? 'font-medium text-green-600 dark:text-green-400'
                          : automation.stats.successRate >= 70
                            ? 'font-medium text-yellow-600 dark:text-yellow-400'
                            : 'font-medium text-red-600 dark:text-red-400'
                      }
                    >
                      {formatPercentage(automation.stats.successRate)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <div className="flex items-center text-muted-foreground">
                      <Timer className="h-4 w-4 mr-1" />
                      <span>Tempo Economizado</span>
                    </div>
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">
                      {formatTimeSaved(automation.stats.totalTimeSavedMinutes)}
                    </span>
                  </div>
                  {automation.stats.lastExecution && (
                    <div className="flex items-center justify-between text-sm mt-2">
                      <div className="flex items-center text-muted-foreground">
                        <Clock className="h-4 w-4 mr-1" />
                        <span>Última Execução</span>
                      </div>
                      <span className="text-card-foreground">
                        {formatRelativeTime(automation.stats.lastExecution.startedAt)}
                      </span>
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div className="space-y-2 border-t pt-4">
                  {/* Action Buttons */}
                  <div className="flex space-x-2 pb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openExecuteModal(automation)}
                      disabled={isExecuting}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Executar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openToggleModal(automation)}
                      disabled={isToggling}
                    >
                      {automation.isActive ? (
                        <Pause className="h-4 w-4" />
                      ) : (
                        <Play className="h-4 w-4" />
                      )}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => openEditModal(automation)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openDeleteModal(automation)}
                      disabled={isDeleting}
                    >
                      <Trash2 className="h-4 w-4 text-red-600 dark:text-red-400" />
                    </Button>
                  </div>

                  {/* View Details Button */}
                  <Link to={`/automations/${automation.id}`}>
                    <Button variant="default" size="sm" className="w-full">
                      <Eye className="h-4 w-4 mr-2" />
                      Ver Detalhes
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center space-x-2">
              <Zap className="h-5 w-5" />
              <span>Automações</span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <SortableTable data={filteredAutomations}>
              {(sortedData) => (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead sortable sortKey="name">
                        Nome
                      </TableHead>
                      <TableHead sortable sortKey="type">
                        Tipo
                      </TableHead>
                      <TableHead sortable sortKey="isActive">
                        Status
                      </TableHead>
                      <TableHead sortable sortKey="stats.totalExecutions" className="text-center">
                        Execuções
                      </TableHead>
                      <TableHead sortable sortKey="stats.successRate" className="text-center">
                        Taxa de Sucesso
                      </TableHead>
                      <TableHead
                        sortable
                        sortKey="stats.totalTimeSavedMinutes"
                        className="text-center"
                      >
                        Tempo Economizado
                      </TableHead>
                      <TableHead sortable sortKey="stats.lastExecution.startedAt">
                        Última Execução
                      </TableHead>
                      <TableHead className="text-right">Ações</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedData.map((automation) => (
                      <TableRow key={automation.id}>
                        <TableCell>
                          <div className="space-y-1">
                            <p className="font-medium text-foreground">{automation.name}</p>
                            {automation.description && (
                              <p className="text-xs text-muted-foreground line-clamp-1">
                                {automation.description}
                              </p>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <AutomationTypeBadge type={automation.type} size="sm" />
                        </TableCell>
                        <TableCell>
                          <Badge variant={automation.isActive ? 'success' : 'error'}>
                            {automation.isActive ? 'Ativa' : 'Inativa'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center font-medium">
                          {automation.stats.totalExecutions}
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={
                              automation.stats.successRate >= 90
                                ? 'font-medium text-green-600 dark:text-green-400'
                                : automation.stats.successRate >= 70
                                  ? 'font-medium text-yellow-600 dark:text-yellow-400'
                                  : 'font-medium text-red-600 dark:text-red-400'
                            }
                          >
                            {formatPercentage(automation.stats.successRate)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center font-medium text-emerald-600 dark:text-emerald-400">
                          {formatTimeSaved(automation.stats.totalTimeSavedMinutes)}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {automation.stats.lastExecution
                            ? formatRelativeTime(automation.stats.lastExecution.startedAt)
                            : '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center justify-end space-x-1">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openExecuteModal(automation)}
                              disabled={isExecuting}
                              className="h-8 w-8 p-0"
                              title="Executar"
                            >
                              <Play className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openToggleModal(automation)}
                              disabled={isToggling}
                              className="h-8 w-8 p-0"
                              title={automation.isActive ? 'Pausar' : 'Ativar'}
                            >
                              {automation.isActive ? (
                                <Pause className="h-3.5 w-3.5" />
                              ) : (
                                <Play className="h-3.5 w-3.5" />
                              )}
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditModal(automation)}
                              className="h-8 w-8 p-0"
                              title="Editar"
                            >
                              <Edit className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openDeleteModal(automation)}
                              disabled={isDeleting}
                              className="h-8 w-8 p-0"
                              title="Deletar"
                            >
                              <Trash2 className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />
                            </Button>
                            <Link to={`/automations/${automation.id}`}>
                              <Button
                                variant="default"
                                size="sm"
                                className="h-8 w-8 p-0"
                                title="Ver detalhes"
                              >
                                <Eye className="h-3.5 w-3.5" />
                              </Button>
                            </Link>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </SortableTable>
          </CardContent>
        </Card>
      )}

      {/* Create Form */}
      <AutomationForm open={isCreateOpen} onOpenChange={setIsCreateOpen} />

      {/* Edit, Execute, Toggle, Delete Modals from hook */}
      {modals}
    </div>
  );
}
